import { Pagination as AranduPagination, IconButton } from '@ads/components-react';
import { ChevronDown, ChevronRight, ChevronUp, ChevronUpDown } from '@ads/icons';
import { useTranslation } from '@linka/i18n';
import { type ColumnDef, flexRender, getCoreRowModel, type Row, useReactTable } from '@tanstack/react-table';
import { Fragment, type JSX, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table/table';

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isPending?: boolean;
  perPage?: number;
  total?: number;
  ariaLabel?: string;
  expandedContent?: (row: TData) => JSX.Element;
  orderBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (columnId: string, direction: 'asc' | 'desc') => void;
  page?: number;
  onPageChange?: (page: number) => void;
}

const DataTablePagination = ({
  perPage,
  total,
  page = 1,
  onPageChange,
}: {
  perPage: number;
  total: number;
  page?: number;
  onPageChange?: (page: number) => void;
}) => {
  const t = useTranslation();
  const totalPages = Math.ceil(total / perPage);
  const isFirstPage = page === 1;
  const isLastPage = totalPages === page;

  if (total <= perPage) return null;

  return (
    <AranduPagination
      onClickPaginationItem={(p) => onPageChange?.(++p)}
      className='overflow-x-auto sm:overflow-x-visible py-micro sm:py-0'
      setActiveIndex={() => null}
      ellipsisLabel={t('components.pagination.more')}
      controlText={{
        previous: isFirstPage ? '' : t('components.pagination.previous'),
        next: isLastPage ? '' : t('components.pagination.next'),
      }}
      activeIndex={page - 1}
      pageAmount={totalPages}
    />
  );
};

export const DataTable = <TData, TValue>({
  columns,
  data,
  isPending,
  total = 0,
  perPage = 6,
  ariaLabel,
  expandedContent,
  orderBy,
  sortOrder,
  onSortChange,
  page,
  onPageChange,
}: DataTableProps<TData, TValue>) => {
  const t = useTranslation();
  const [expandedRowData, setExpandedRowData] = useState<Row<TData> | null>(null);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const hasData = table.getRowModel().rows.length > 0;
  const rows = table.getRowModel().rows;
  const colSpan = expandedContent ? table.getAllColumns().length + 1 : table.getAllColumns().length;

  const expandRow = (row: Row<TData>) => {
    if (!expandedContent) return null;
    setExpandedRowData(expandedRowData?.id === row.id ? null : row);
  };

  const renderSortingIcon = (columnId: string) => {
    if (!orderBy || orderBy !== columnId) return ChevronUpDown;
    return sortOrder === 'asc' ? ChevronUp : ChevronDown;
  };

  return (
    <div className='overflow-hidden rounded-md border border-ctx-layout-border'>
      <div className='overflow-x-auto'>
        <Table aria-label={ariaLabel}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {expandedContent && <TableHead />}
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    <div className='flex items-center gap-quark'>
                      <div className='flex-1'>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                      {header.column.getCanSort() && onSortChange && (
                        <IconButton
                          onClick={() => {
                            const isAsc = sortOrder === 'asc';
                            onSortChange(header.column.id, isAsc ? 'desc' : 'asc');
                          }}
                          icon={renderSortingIcon(header.column.id)}
                          hierarchy='tertiary'
                          size='sm'
                          ariaLabel='Ordenar'
                        />
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {!isPending &&
              (hasData ? (
                rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow>
                      {expandedContent && (
                        <TableCell className='cursor-pointer' onClick={() => expandRow(row)}>
                          {expandedRowData?.id === row.id ? (
                            <ChevronDown className='size-[1em]' />
                          ) : (
                            <ChevronRight className='size-[1em]' />
                          )}
                        </TableCell>
                      )}
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                      ))}
                    </TableRow>
                    {expandedContent && row.id === expandedRowData?.id && (
                      <TableRow key={row.id} className='!bg-ctx-layout-body'>
                        <TableCell colSpan={colSpan}>{expandedContent(row.original)}</TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className='text-center h-xxxl'>
                    {t('empty')}
                  </TableCell>
                </TableRow>
              ))}

            {isPending &&
              Array.from({ length: 6 }).map((_, index) => (
                <TableRow key={`skeleton-row-${String(index)}`}>
                  {Array.from({ length: colSpan - 1 }).map((_, cellIndex) => (
                    <TableCell key={`skeleton-cell-${index}-${String(cellIndex)}`}>
                      <div className='h-[1.25rem] w-full bg-base-gray-200 rounded-md animate-pulse' />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <footer className='p-mini'>
        <DataTablePagination perPage={perPage} total={total} page={page} onPageChange={onPageChange} />
      </footer>
    </div>
  );
};

export type { ColumnDef };
